import React, { FunctionComponent, useState, useEffect, useContext, useCallback } from 'react';
import { useHistory } from 'react-router-dom';
import { makeStyles } from '@mui/styles';
import {
  Autocomplete,
  Button,
  Card,
  CardContent,
  Grid,
  Link,
  TextField,
  Box,
  CircularProgress,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import IconButton from '@mui/material/IconButton';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import TextInput from '../../form/components/TextInput';
import SelectInput from '../../form/components/SelectInput';
import { EstimateCustomersContext } from '../contexts/EstimateCustomersContext';
import { EstimateDetailsContext } from '../../estimateDetails/contexts/EstimateDetailsContext';
import { SalesEstimateCustomerFormValues } from '../model';
import DataTable from '../../dataTable/DataTable/DataTable';
import { IDataTableColumn } from '../../dataTable/DataTable/model';
import axios from '../../utils/axios.utils';

const columns: IDataTableColumn<any>[] = [
  {
    name: 'Asset',
    selector: 'asset_number',
    sortable: true,
  },
  {
    name: 'Manufacturer',
    selector: 'manufacturer',
    sortable: true,
  },
  {
    name: 'Model',
    selector: 'model_number',
    sortable: true,
  },
  {
    name: 'Serial',
    selector: 'serial_number',
    sortable: true,
  },
  {
    name: 'Age',
    selector: 'vintage',
    sortable: true,
  },
  {
    name: 'V/P',
    selector: 'phase',
    sortable: true,
    cell: (row) => <span>{row.voltage && row.phase ? `${row.voltage}/${row.phase}` : 'Unknown'}</span>,
  },
  {
    name: 'Degraded EER',
    selector: 'phase',
    sortable: true,
    cell: (row) => <span>N/A</span>,
  },
];

interface AssetFormProps {
  salesEstimateId: string;
}

const AssetForm = (props: AssetFormProps) => {
  const { salesEstimateId } = props;

  const history = useHistory();

  const { siteAssets } = useContext(EstimateCustomersContext);
  const { salesEstimate } = useContext(EstimateDetailsContext);
  const [formValues, setFormValues] = React.useState<SalesEstimateCustomerFormValues>({});
  const [loading, setLoading] = React.useState(false);
  const [xoiButtonText, setXoiButtonText] = React.useState('Import From XOi');

  const [formOptionsFetched, setFormOptionsFetched] = useState<boolean>(false);

  const assetsInitialized = React.useRef(false);

  const [formOptions, setFormOptions] = useState<any>({});
  const [isSubmitting, setSubmitting] = useState<boolean>(false);
  const [selectedIds, setSelectedIds] = useState(new Set());

  const fetchFormOptions = async () => {
    try {
      await axios.get('assets/new').then((response: any) => {
        setFormOptions(response.data);
      });
    } catch (error) {
      console.log(error);
    }
  };

  useEffect(() => {
    if (!formOptionsFetched) {
      fetchFormOptions();
      setFormOptionsFetched(true);
    }
  }, [formOptionsFetched]);

  const { control, handleSubmit, register, watch } = useForm({
    defaultValues: {
      name: '',
      sales_estimate_id: salesEstimateId,
      assets: [],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'assets',
  });

  useEffect(() => {
    if (!assetsInitialized.current) {
      assetsInitialized.current = true;

      setSelectedIds(new Set(salesEstimate?.sales_estimate_assets?.map((asset) => asset?.asset_id)));

      salesEstimate?.sales_estimate_assets?.forEach((asset, index) => {
        const assetExists = fields.some((field) => field.internal_id === asset.asset_id);
        if (!assetExists) {
          append({
            internal_id: asset.asset_id,
            manufacturer_id: asset.manufacturer_id,
            asset_number: asset.asset_number,
            model_number: asset.model_number,
            serial_number: asset.serial_number,
            age: asset.vintage,
          });
        }
      });
    }
  }, []);

  const handleUploadToXoi = () => {
    if (xoiButtonText === 'Import From XOi') {
      setLoading(true);

      window.open('https://visionweb.xoi.io/jobactivity/cjob-i9caa242aeac74db9ba152f149947fde6-094e', '_blank');

      setTimeout(() => {
        setLoading(false);
        setXoiButtonText('Add 1 Asset(s) from XOi');
      }, 120000);
    } else if (xoiButtonText === 'Add 1 Asset(s) from XOi') {
      append({
        // manufacturer_id: 2,
        // asset_number: 'New Unit',
        // model_number: 'TTB036C100A2',
        // serial_number: 'R4628MEBF',
        // age: '14',
      });
    }
  };

  const syncFormWithSelected = (selectedIds) => {
    // This function would clear the current assets and append only selected ones
    if (!assetsInitialized.current) return;

    remove();

    siteAssets.forEach((asset) => {
      const assetExists = fields.some((field) => {
        return field.internal_id === asset.asset_id;
      });

      if (selectedIds.has(asset.asset_id)) {
        append({
          internal_id: asset.asset_id,
          manufacturer_id: asset.manufacturer_id,
          asset_number: asset.asset_number,
          model_number: asset.model_number,
          serial_number: asset.serial_number,
          age: asset.vintage,
        });
      }
    });
  };

  const handleRowSelected = useCallback(
    (state) => {
      const newSelectedIds = new Set(selectedIds);

      state.selectedRows.forEach((row) => {
        if (newSelectedIds.has(row.asset_id)) {
          // newSelectedIds.delete(row.asset_id);
        } else {
          newSelectedIds.add(row.asset_id);
        }
      });

      setSelectedIds(newSelectedIds);
      syncFormWithSelected(newSelectedIds);
    },
    [selectedIds],
  );

  const toggleCleared = () => {
    remove();
  };

  const handleRemove = (index, itemId) => {
    remove(index);
    setSelectedIds((prevIds) => {
      const newIds = new Set(prevIds);
      newIds.delete(itemId);
      return newIds;
    });
  };

  const rowSelectCritera = (row) => {
    return selectedIds.has(row.asset_id);
  };

  const onSubmit = (data) => {
    setSubmitting(true);

    axios
      .post(`sales_estimates/${salesEstimateId}/sales_estimate_assets`, data)
      .then((response) => {
        history.push(`/rooftop_automation/${salesEstimateId}/equipment`);
      })
      .catch((error) => {
        setSubmitting(false);
        console.error('Error posting data:', error);
      });
  };

  return (
    formOptions?.manufacturer_choices &&
    assetsInitialized.current && (
      <form onSubmit={handleSubmit(onSubmit)}>
        <br />
        <Grid container justifyContent="space-between">
          <Grid item>
            <h2 style={{ fontSize: '22px', marginBottom: 10, paddingTop: 5 }}>System Details</h2>
          </Grid>
          <Grid item>
            <Grid container alignItems="center">
              {loading && (
                <>
                  <CircularProgress size={24} />
                  <Typography style={{ marginLeft: 10, marginRight: 10 }}>Waiting for tag data from XOi...</Typography>
                </>
              )}
              <Button variant="contained" color="primary" onClick={handleUploadToXoi}>
                {xoiButtonText}
              </Button>
            </Grid>
          </Grid>
        </Grid>
        <div style={{ margin: '20px 0' }}>
          <SelectInput label="Estimate Type" value={'RTU'} choices={[{ label: 'Rooftop Units (RTU)', value: 'RTU' }]} />
        </div>
        <br />
        <DataTable
          title="Assets"
          columns={columns}
          data={siteAssets.filter((asset) => asset.asset_number?.includes('RTU-'))}
          striped
          dense
          selectableRows
          highlightOnHover
          pointerOnHover
          noHeader
          onSelectedRowsChange={handleRowSelected}
          clearSelectedRows={toggleCleared}
          selectableRowSelected={rowSelectCritera}
        />
        <br />
        {fields.map((item, index) => (
          <Card style={{ marginBottom: 20, position: 'relative', overflow: 'visible' }} key={`field-${index}`}>
            <IconButton
              style={{
                position: 'absolute',
                right: -10,
                top: -10,
                color: 'white',
                backgroundColor: 'red',
                borderRadius: '50%',
                padding: '4px',
                zIndex: 999,
                boxShadow: '0px 2px 4px rgba(0,0,0,0.5)',
              }}
              onClick={() => {
                handleRemove(index, item.internal_id);
              }}
              aria-label="close"
            >
              <CloseIcon />
            </IconButton>
            <CardContent style={{ padding: '15px 30px 15px 15px' }}>
              <Grid key={item.id} container spacing={3}>
                <Grid item xs={12}>
                  <strong>Asset {index + 1}</strong>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Controller
                    name={`assets.${index}.asset_number`}
                    control={control}
                    render={({ field }) => <TextField {...field} label="Asset Number" variant="outlined" fullWidth />}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <div style={{ width: '100%' }}>
                    <Controller
                      name={`assets.${index}.manufacturer_id`}
                      control={control}
                      render={({ field }) => (
                        <>
                          <Autocomplete
                            disablePortal
                            options={formOptions?.manufacturer_choices}
                            getOptionLabel={(option: any) => option.label}
                            sx={{ width: '100%' }}
                            value={formOptions?.manufacturer_choices?.find((option) => option.value === field.value)}
                            onChange={(event, newValue) => {
                              field.onChange(newValue?.value || '');
                            }}
                            renderInput={(params) => <TextField {...params} label="Manufacturer" />}
                          />
                        </>
                      )}
                    />
                  </div>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Controller
                    name={`assets.${index}.age`}
                    control={control}
                    render={({ field }) => <TextField {...field} label="Age (Years)" variant="outlined" fullWidth />}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Controller
                    name={`assets.${index}.model_number`}
                    control={control}
                    render={({ field }) => <TextField {...field} label="Model" variant="outlined" fullWidth />}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <Controller
                    name={`assets.${index}.serial_number`}
                    control={control}
                    render={({ field }) => <TextField {...field} label="Serial" variant="outlined" fullWidth />}
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        ))}

        <Button variant="contained" onClick={() => append({ asset_number: 'New Unit (Set after save)' })} type="button">
          Add Asset
        </Button>

        <br />
        <hr />
        <br />

        <Button variant="contained" color="primary" onClick={handleSubmit(onSubmit)} disabled={isSubmitting}>
          {isSubmitting ? 'Submitting assets...' : 'Submit Assets'}
        </Button>
      </form>
    )
  );
};

export default AssetForm;
